import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Box, useTheme } from '@mui/material';
import { EzCard } from '../EzCard';
import { EzFooter, EzContent, EzHeader } from '../EzContent';
import { ScrollLock } from './ScrollLock';
import { SlotProvider, containsChild, hasContentSlot } from '../../utils/slots';
import { useUniqueId, useTranslation } from '../../utils/hooks';
import EzButton from '../EzButton';
import EzHeading from '../EzHeading';
import EzLayout from '../EzLayout';
import EzPortal from '../EzPortal';
import FocusScope from '../utils/FocusScope';
import theme from '../theme.config';
import en from './en';
import EzIconButton from '../EzIconButton';
import EzIcon from '../EzIcon';
var HEADER_ID = 'modal-header';
var SUBMIT_BUTTON_ID = 'modal-submit-button';
var DISMISS_BUTTON_ID = 'modal-dismiss-button';
var CLOSE_BUTTON_ID = 'modal-close-button';
var CARD_ID = 'modal-card';
var MODAL_IDS = [HEADER_ID, SUBMIT_BUTTON_ID, DISMISS_BUTTON_ID, CLOSE_BUTTON_ID, CARD_ID];
var overlay = theme.css({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$modal-overlay-bg',
  zIndex: '$modal-z'
});
var dialog = theme.css({
  border: '0 !important',
  height: '$full',
  width: '$full',
  outline: 'none',
  overflowY: 'auto',
  position: 'relative',
  '&:focus': {
    boxShadow: '$focus-ring'
  },
  '@medium': {
    height: 'auto',
    maxHeight: '$modal-height',
    '&&': {
      width: '$modal-width'
    }
  }
});
var header = theme.css({
  justifyContent: 'space-between',
  '& + *': {
    borderTop: '1px solid $modal-border'
  },
  '&&': {
    padding: '$modal-tray-py $modal-tray-px'
  },
  '@medium': {
    '& + *': {
      borderStyle: 'none'
    },
    '&&': {
      padding: '$modal-py $modal-px'
    }
  }
});
var body = theme.css({
  flexGrow: 1,
  overflowY: 'auto',
  '&&': {
    padding: '$modal-tray-py $modal-tray-px'
  },
  '@medium': {
    '&&': {
      padding: '0 $modal-px $modal-py'
    }
  }
});
var footer = theme.css({
  '&&': {
    padding: '$modal-tray-py $modal-tray-px'
  },
  '@medium': {
    '&&': {
      padding: '$modal-py $modal-px'
    }
  }
});
var useDialogBackdrop = function useDialogBackdrop(_ref) {
  var onDismiss = _ref.onDismiss;
  return {
    onClick: function onClick(e) {
      return e.target === e.currentTarget && onDismiss();
    }
  };
};
var useDialog = function useDialog(_ref2) {
  var onDismiss = _ref2.onDismiss;
  return {
    role: 'dialog',
    'aria-modal': true,
    tabIndex: -1,
    onKeyDown: function onKeyDown(event) {
      if (event.defaultPrevented) return;
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onDismiss();
    }
  };
};
var EzModal = function EzModal(_ref3) {
  var children = _ref3.children,
    headerText = _ref3.headerText,
    destructive = _ref3.destructive,
    dismissLabel = _ref3.dismissLabel,
    isOpen = _ref3.isOpen,
    isSubmitting = _ref3.isSubmitting,
    onDismissHandler = _ref3.onDismiss,
    onSubmit = _ref3.onSubmit,
    overrideDefaultFocus = _ref3.overrideDefaultFocus,
    submitLabel = _ref3.submitLabel;
  var labelId = useUniqueId();
  var isDismissable = Boolean(onDismissHandler);
  var onDismiss = isDismissable ? onDismissHandler : function () {};
  var backdrop = useDialogBackdrop({
    onDismiss: onDismiss
  });
  var dialogProps = useDialog({
    onDismiss: onDismiss
  });
  var _useTranslation = useTranslation(en),
    t = _useTranslation.t;
  var emotionTheme = useTheme();
  var hasPicture = containsChild(children, 'EzPreview');
  if (!isOpen) return null;
  return React.createElement(EzPortal, null, React.createElement("div", _extends({
    className: overlay()
  }, backdrop), React.createElement(ScrollLock, null), React.createElement(FocusScope, {
    contain: true,
    restoreFocus: true,
    autoFocus: !overrideDefaultFocus,
    modalIds: MODAL_IDS
  }, React.createElement(SlotProvider, {
    slots: {
      header: {
        className: header()
      },
      content: {
        className: body()
      },
      footer: {
        className: footer()
      }
    }
  }, React.createElement(EzCard, _extends({}, dialogProps, {
    className: dialog(),
    "aria-labelledby": labelId,
    id: CARD_ID
  }), headerText && React.createElement(EzHeader, null, React.createElement(EzHeading, {
    size: "2",
    id: labelId
  }, React.createElement(Box, {
    id: HEADER_ID,
    color: emotionTheme.tokens['color-text-default']
  }, headerText))), hasContentSlot(children) ? children : React.createElement(EzContent, null, children), (submitLabel || dismissLabel) && React.createElement(EzFooter, null, React.createElement(EzLayout, {
    layout: {
      base: 'stack',
      medium: 'basic'
    }
  }, submitLabel && React.createElement(EzButton, {
    id: SUBMIT_BUTTON_ID,
    color: destructive ? 'destructive' : undefined,
    onClick: onSubmit,
    loading: isSubmitting
  }, submitLabel), dismissLabel && React.createElement(EzButton, {
    id: CLOSE_BUTTON_ID,
    disabled: isSubmitting,
    onClick: onDismiss,
    variant: onSubmit ? 'outlined' : 'filled'
  }, dismissLabel))), isDismissable && React.createElement(Box, {
    id: CLOSE_BUTTON_ID,
    position: hasPicture ? 'absolute' : undefined,
    right: hasPicture ? 0 : undefined,
    top: hasPicture ? 0 : undefined,
    margin: emotionTheme.tokens['spacing-5'],
    gridArea: hasPicture ? undefined : 'close',
    sx: {
      '& button': {
        boxShadow: hasPicture ? emotionTheme.tokens['shadow-md'] : undefined,
        '&:hover': {
          bgcolor: emotionTheme.tokens['color-action-default-hovered']
        }
      }
    }
  }, React.createElement(EzIconButton, {
    ariaLabel: dismissLabel || t('dismiss'),
    color: "common.white",
    onClick: onDismiss
  }, React.createElement(EzIcon, {
    icon: faXmark,
    color: "common.black"
  }))))))));
};
export default EzModal;